import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  listPhonebooks,
  saveChatContact,
  getWhatsAppErrorMessage,
  unwrapWhatsAppList,
} from "../../../services/whatsappApi";
import {
  formatWhatsAppMobile,
  validateWhatsAppMobileForSubmit,
} from "../../../utils/whatsappFormat";
import {
  sanitizeMobileInput,
  mobileInputFieldProps,
} from "../../../utils/mobileInput";
import {
  hasWhatsAppPermission,
  WHATSAPP_PERMISSION_CODES,
} from "../../../utils/whatsappPermissions";

const EMPTY_VARS = { var1: "", var2: "", var3: "", var4: "", var5: "" };

export default function SaveContactModal({
  show,
  onHide,
  chatId,
  initialName = "",
  initialMobile = "",
  initialPhonebookId = "",
  initialVars = EMPTY_VARS,
  onSaved,
}) {
  const canLoadPhonebooks = hasWhatsAppPermission(
    WHATSAPP_PERMISSION_CODES.PHONEBOOK,
  );

  const [phonebooks, setPhonebooks] = useState([]);
  const [loadingPb, setLoadingPb] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [phonebookId, setPhonebookId] = useState("");
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [vars, setVars] = useState(EMPTY_VARS);

  useEffect(() => {
    if (!show) return;
    setName(initialName || "");
    const digits = String(initialMobile || "").replace(/\D/g, "");
    setMobile(digits.length >= 10 ? digits.slice(-10) : digits);
    setPhonebookId(
      initialPhonebookId != null && initialPhonebookId !== ""
        ? String(initialPhonebookId)
        : "",
    );
    setVars({
      var1: initialVars?.var1 || "",
      var2: initialVars?.var2 || "",
      var3: initialVars?.var3 || "",
      var4: initialVars?.var4 || "",
      var5: initialVars?.var5 || "",
    });
    setFormError("");
  }, [show, initialName, initialMobile, initialPhonebookId, initialVars]);

  useEffect(() => {
    if (!show || !canLoadPhonebooks) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingPb(true);
      try {
        const res = await listPhonebooks();
        if (!cancelled) setPhonebooks(unwrapWhatsAppList(res));
      } catch (err) {
        if (!cancelled) {
          toast.error(
            getWhatsAppErrorMessage(err, "Failed to load phonebooks"),
          );
        }
      } finally {
        if (!cancelled) setLoadingPb(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, canLoadPhonebooks]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");

    if (!chatId) return;
    if (!phonebookId) {
      setFormError("Select a phonebook");
      return;
    }
    if (!name.trim()) {
      setFormError("Name is required");
      return;
    }

    const mobileCheck = validateWhatsAppMobileForSubmit(mobile);
    if (!mobileCheck.ok) {
      setFormError(mobileCheck.error);
      return;
    }

    setSaving(true);
    try {
      const res = await saveChatContact(chatId, {
        phonebook_id: Number(phonebookId),
        name: name.trim(),
        mobile: mobileCheck.mobile,
        var1: vars.var1 || "",
        var2: vars.var2 || "",
        var3: vars.var3 || "",
        var4: vars.var4 || "",
        var5: vars.var5 || "",
      });
      if (res?.success === false) {
        throw new Error(res.message || "Failed to save contact");
      }
      toast.success(
        res?.message && res.message !== "radha" ? res.message : "Contact saved",
      );
      onHide();
      if (typeof onSaved === "function") onSaved(res);
    } catch (err) {
      const msg = getWhatsAppErrorMessage(err, "Failed to save contact");
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            <Icon
              icon="solar:user-plus-line-duotone"
              width={20}
              className="me-1"
            />
            Save to phonebook
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError ? (
            <div className="alert alert-danger py-2">{formError}</div>
          ) : null}

          {!canLoadPhonebooks ? (
            <div className="alert alert-warning py-2">
              You need phonebook permission to save contacts.
            </div>
          ) : null}

          <Form.Group className="mb-3">
            <Form.Label>Phonebook *</Form.Label>
            <Form.Select
              value={phonebookId}
              onChange={(e) => setPhonebookId(e.target.value)}
              disabled={loadingPb || !canLoadPhonebooks}
              required
            >
              <option value="">Select phonebook</option>
              {phonebooks.map((pb) => (
                <option key={pb.id} value={pb.id}>
                  {pb.name}
                  {pb.contactCount != null ? ` (${pb.contactCount})` : ""}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <Form.Control
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Mobile *</Form.Label>
            <Form.Control
              {...mobileInputFieldProps}
              value={mobile}
              onChange={(e) => setMobile(sanitizeMobileInput(e.target.value))}
              placeholder={
                formatWhatsAppMobile(initialMobile) || "10-digit mobile"
              }
              required
            />
          </Form.Group>

          <div className="row g-2">
            {["var1", "var2", "var3", "var4", "var5"].map((key, idx) => (
              <div key={key} className="col-6">
                <Form.Group>
                  <Form.Label>Var {idx + 1}</Form.Label>
                  <Form.Control
                    value={vars[key]}
                    onChange={(e) =>
                      setVars((prev) => ({ ...prev, [key]: e.target.value }))
                    }
                  />
                </Form.Group>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="success"
            disabled={saving || !canLoadPhonebooks}
          >
            {saving ? "Saving..." : "Save contact"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
